import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { frApi, errMsg, toPayload } from "./frUtils";
import RecommendForm from "./RecommendForm";
import ResultCard from "./ResultCard";
import NegotiationCard from "./NegotiationCard";

const thisMonth = () => new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" }).slice(0, 7);
const EMPTY = {
    parent_name: "", student_name: "", phone: "", lead_id: null, tuition_mode: "online_india", calc_type: "monthly", city: "", grade: "", board: "", subject: "", category_id: "", special: "", common_teacher: true,
    duration: 60, sessions_per_week: 3, weekdays: [], month: thisMonth(), monthly_sessions_override: "",
    budget_amount: "", budget_basis: "per_session", proposed_fee: "", final_fee: "", notes: "",
};

const LIVE_KEYS = new Set(["parent_name", "student_name", "phone", "budget_amount", "budget_basis", "proposed_fee", "final_fee", "notes"]);


export default function RecommendTab({ boot, prefill }) {
    const [form, setForm] = useState(EMPTY);
    const [result, setResult] = useState(null);
    const [busy, setBusy] = useState(false);
    const [record, setRecord] = useState(null);
    const auto = useRef(false);

    useEffect(() => {
        if (!prefill) return;
        const lb = String(prefill.board || "").trim().toLowerCase();
        const bk = lb && (boot.boards || []).find((b) => [b.key, b.name, ...(b.aliases || [])].some((a) => a.toLowerCase() === lb))?.key;
        const abroad = prefill.country && !/^\s*(india|in|bharat)\s*$/i.test(prefill.country);
        const mode = abroad ? { tuition_mode: "foreign", calc_type: "per_session", city: prefill.country } : {};
        setForm({ ...EMPTY, ...Object.fromEntries(Object.entries(prefill).filter(([, v]) => v != null && v !== "")), ...(bk ? { board: bk } : {}), ...mode });
        setResult(null); setRecord(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prefill]);

    const [stale, setStale] = useState(false);
    const set = (patch) => {
        setForm((f) => ({ ...f, ...patch })); setRecord(null);
        if (result && Object.keys(patch).some((k) => !LIVE_KEYS.has(k))) setStale(true);
    };

    const run = async (f = form, quiet = false) => {
        if (!f.city.trim()) { toast.error(f.tuition_mode === "foreign" ? "Enter the student's country first" : "Enter the parent's city first"); return; }
        if (!quiet) setBusy(true);
        try {
            const r = await frApi.post("/recommend", toPayload(f));
            setResult(r); auto.current = true; if (!quiet) setStale(false);
        } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
    };

    // Budget / fee edits re-check silently once a recommendation exists.
    const { budget_amount, budget_basis, proposed_fee } = form;
    useEffect(() => {
        if (!auto.current) return undefined;
        const t = setTimeout(() => run(form, true), 450);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [budget_amount, budget_basis, proposed_fee]);

    return (
        <div className="grid xl:grid-cols-[minmax(0,5fr)_minmax(0,6fr)] gap-5 items-start" data-testid="fee-rec-recommend">
            <RecommendForm form={form} set={set} boot={boot} busy={busy} onGenerate={() => run()}
                specialOptions={result?.pricing?.special_options || []} onReset={() => { setForm(EMPTY); setResult(null); setRecord(null); setStale(false); auto.current = false; }} />
            <div className="space-y-5 min-w-0">
                {stale && <p className="text-xs font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="fee-rec-stale">Details changed. Tap Generate Fee Recommendation again to update the range.</p>}
                <ResultCard result={result} />
                {result?.status === "ok" && (
                    <NegotiationCard form={form} set={set} result={result} record={record} setRecord={setRecord} />
                )}
            </div>
        </div>
    );
}
